import { Injectable } from '@angular/core';
// date-fns
import { format, isAfter, isToday, isValid } from 'date-fns';
import { flatMap as _flatmap, get as _get, some as _some } from 'lodash';

import { BillingNotificationsState } from '@amfam/billing/billaccount/data-access';
import { PolicyTypeIconConstants } from '@amfam/policy/models';
import {
  AutomaticPayment,
  BillAccount,
  BillAccountModel,
  EntityNotification,
  ONLINE_BILLING,
  Payment,
  PaymentAccount,
  PaymentStatus,
  ScheduledPayment
} from '@amfam/shared/models';

import {
  BillAccountActionButtons,
  BillAccountCardMetadata,
  BillAccountCardStatus,
  BillAccountPillType,
  BillAccountStatusIcons,
  BillAccountStatusText
} from '../models/billing-overview.models';

@Injectable({ providedIn: 'root' })
export class OverviewBillingUtilService {
  constructor() {}

  public getBillAccountCardMetadata(
    billAccount: BillAccount,
    billAccountNotifications: BillingNotificationsState[],
    billAccountsNotEligibleForAutoPay: BillAccount[],
    scheduledPayments: ScheduledPayment[],
    successfulPayments: ScheduledPayment[],
    failedPayments: ScheduledPayment[],
    automaticPayments: AutomaticPayment[],
    automaticPaymentNotifications: EntityNotification[],
    paymentAccounts: PaymentAccount[],
    paymentHistory: Payment[],
    isRomeNew: boolean,
    isRomeOld: boolean
  ): BillAccountCardMetadata {
    const matchingScheduledPayments: ScheduledPayment[] = this.getScheduledPaymentsForBillAccount(
      billAccount.billAccountNumber,
      scheduledPayments
    );
    const matchingAutomaticPayment: AutomaticPayment = this.getAutomaticPaymentForBillAccount(
      billAccount.billAccountNumber,
      automaticPayments
    );
    const matchingAutomaticPaymentNotification = automaticPaymentNotifications.find(
      notification => notification.id === billAccount.billAccountNumber
    );
    let allPayments: Array<ScheduledPayment | AutomaticPayment> = matchingScheduledPayments;
    if (matchingAutomaticPayment) {
      allPayments = allPayments.concat(matchingAutomaticPayment);
    }
    const billAccountNotification: BillingNotificationsState = billAccountNotifications.find(
      (notification: BillingNotificationsState) =>
        notification.billAccountNumber === billAccount.billAccountNumber
    );

    const hasUnsuccessfulPayment = this.hasUnsuccessfulPayment(
      billAccount,
      [...scheduledPayments, ...successfulPayments],
      failedPayments,
      [matchingAutomaticPayment]
    );

    let pillVariant;
    let pillText;
    if (isRomeNew) {
      pillVariant = 'alert';
      pillText = 'New Billing Account';
    } else if (isRomeOld) {
      pillVariant = 'success';
      pillText = 'Old Billing Account';
    }

    return {
      billAccountNumber: _get(billAccount, 'billAccountNumber', ''),
      billAccountNickname: _get(billAccount, 'billingPreferences.accountNickName', ''),
      minimumDue: Number(_get(billAccount, 'minimumAmountDue', '')),
      minimumDueDate: _get(billAccount, 'paymentDueDate', ''),
      accountBalance: _get(billAccount, 'accountBalance', ''),
      hasAFT: _get(billAccount, 'enabledAFT', false),
      hasUnsuccessfulPayment: hasUnsuccessfulPayment,
      isPastDue: _get(billAccount, 'pastDue', false),
      hasLoadPreferencesError: _get(billAccountNotification, 'loadPrefsError', false),
      isOnlineBilling: _get(billAccount, 'billingMethod') === ONLINE_BILLING,
      isRegisteredElseWhere: _get(billAccount, 'registeredElsewhere', false),
      isReadOnly: _get(billAccount, 'readOnly', false),
      hasExpiringPaymentMethod: this.hasExpiredPaymentAccounts(allPayments, paymentAccounts)
        .hasExpiringPaymentMethod,
      hasExpiredPaymentMethod: this.hasExpiredPaymentAccounts(allPayments, paymentAccounts)
        .hasExpiredPaymentMethod,
      expiredOrExpiringPaymentAccounts: this.hasExpiredPaymentAccounts(allPayments, paymentAccounts)
        .expiredOrExpiringPaymentAccounts,
      hasMultipleScheduledPayments: allPayments.length > 1,
      hasSingleScheduledPayment: matchingScheduledPayments.length === 1,
      scheduledPayments: matchingScheduledPayments,
      hasAutoPay: !!matchingAutomaticPayment,
      hasAutoPayError:
        !!matchingAutomaticPaymentNotification &&
        _get(matchingAutomaticPaymentNotification, 'hasError', false),
      automaticPayments: matchingAutomaticPayment,
      hasTPIPayer: this.hasTPIPayer(
        !!matchingAutomaticPayment,
        billAccountsNotEligibleForAutoPay,
        billAccount
      ),
      hasMinimumAmountDue: Number(_get(billAccount, 'minimumAmountDue', 0)) > 0,
      hasPaymentDueToday: isToday(_get(billAccount, 'paymentDueDate')),
      riskDescriptions: _flatmap(billAccount.policyList, policy => policy.riskDescriptionList),
      isAdvance: _get(billAccount, 'isAdvance', false),
      lastPaymentDate: this.getMostRecentPaymentDate(
        paymentHistory,
        _get(billAccount, 'billAccountNumber', '')
      ),
      cancelLetterSent: _get(billAccount, 'cancelLetterSent', false),
      previousAmountDue: _get(billAccount, 'minimumAmountDueBreakdown.previousAmountDue', null),
      policyList: _get(billAccount, 'policyList', null),
      isRomeNew,
      isRomeOld,
      pillVariant,
      pillText,
      multipleScheduledPaymentsCount: allPayments.length > 0 ? allPayments.length : 0
    };
  }

  /**
    This method gives 'Card' object to utilize on billing-overview-cards. This implements a fall-through logic flow in 3 broad categories:
    1. Determine if there are any top-level conditions (hasUnsuccessfulPaymentAlert, AFT, Enrolled elsewhere)
    2. Determine if there are any payments (scheduled or autopay)
    3. Determine state of the bill (past due, due today, due soon, nothing due)
   */
  public getBillAccountCardStatus(
    billAccountMetadata: BillAccountCardMetadata
  ): BillAccountCardStatus {
    // AFT is considered a top-level condition in that we dont' evaluate any payments or whether the bill is past-due
    if (billAccountMetadata.hasAFT) {
      return {
        statusText: '',
        actionButton: null,
        statusIcon: BillAccountStatusIcons.CHECK,
        statusPillText: 'Paid by other',
        statusPillType: BillAccountPillType.SUCCESS
      };
    }

    // Unsuccessfu payment alert is considered a top-level condition - we don't evaluate anything else if true
    if (billAccountMetadata.hasUnsuccessfulPayment) {
      // Unsuccessful and past due
      if (billAccountMetadata.isPastDue) {
        return {
          statusText: BillAccountStatusText.FAILED_PAYMENT_PASTDUE,
          actionButton: BillAccountActionButtons.MAKE_PAYMENT,
          statusIcon: BillAccountStatusIcons.ALERT,
          statusPillText: 'Unsuccessful',
          statusPillType: BillAccountPillType.ERROR
        };
      }
      // Just unsuccessful
      return {
        statusText: BillAccountStatusText.FAILED_PAYMENT,
        actionButton: BillAccountActionButtons.MAKE_PAYMENT,
        statusIcon: BillAccountStatusIcons.ALERT,
        paymentNeeded: true,
        statusPillText: 'Unsuccessful',
        statusPillType: BillAccountPillType.ERROR
      };
    }

    // Provide additional information if preference call is failed.
    if (billAccountMetadata.hasLoadPreferencesError && billAccountMetadata.isOnlineBilling) {
      return {
        statusText: BillAccountStatusText.FAILED_PREFS_LOAD,
        actionButton: null,
        statusIcon: BillAccountStatusIcons.ALERT
      };
    }

    // Registered elsewhere and read only are top-level conditions - we don't evaluate any payments made or the status of the bill account
    if (billAccountMetadata.isRegisteredElseWhere || billAccountMetadata.isReadOnly) {
      if (billAccountMetadata.isPastDue) {
        return {
          statusText: BillAccountStatusText.EXTERNAL_BAM,
          actionButton: null,
          statusIcon: BillAccountStatusIcons.ALERT,
          paymentNeeded: true,
          statusPillText: 'Paid by other',
          statusPillType: BillAccountPillType.ERROR
        };
      } else {
        return {
          statusText: BillAccountStatusText.EXTERNAL_BAM,
          actionButton: null,
          statusIcon: BillAccountStatusIcons.INFO,
          paymentNeeded: true,
          statusPillText: 'Paid by other',
          statusPillType: BillAccountPillType.SUCCESS
        };
      }
    }

    /*
        Determine if any payment exists (Autopay or scheduled payment(s) or both)
        Order of importance:
        1. Has a scheduled payment or automatic payment associated with an expired credit card
        2. Multilple scheduled payments or Single scheduled payment and Autopay.
        3. Show Setup Autopay when someone has a single payment processing
        4. Single scheduled payment
        5. Autopay
      */

    // 1. Check if there is a scheduled or autopay associated with an expired credit card
    if (
      billAccountMetadata.hasExpiredPaymentMethod ||
      billAccountMetadata.hasExpiringPaymentMethod
    ) {
      return {
        statusText: BillAccountStatusText.EXPIRED_PAYMENT_ACCOUNT,
        actionButton: BillAccountActionButtons.EDIT_PAYMENT_ACCOUNT,
        statusIcon: BillAccountStatusIcons.ALERT,
        statusPillText: billAccountMetadata.hasExpiredPaymentMethod
          ? 'Card expired'
          : 'Expiring soon',
        statusPillType: BillAccountPillType.ERROR
      };
    }

    // 2. Check for multiple scheduled payments or single scheduled payment and Autopay
    if (
      billAccountMetadata.hasMultipleScheduledPayments &&
      billAccountMetadata.multipleScheduledPaymentsCount > 1
    ) {
      const isScheduledPayment =
        billAccountMetadata.scheduledPayments[0].paymentStatus === PaymentStatus.SCHEDULED;
      const statusText = isScheduledPayment
        ? BillAccountStatusText.MULTIPLE_SCHEDULED_PAYMENTS
        : BillAccountStatusText.MULTIPLE_PROCESSING_PAYMENTS;
      const upcomingScheduledDate = this.getUpcomingScheduledDate(
        billAccountMetadata?.scheduledPayments
      );
      return {
        statusText: billAccountMetadata.multipleScheduledPaymentsCount + ' ' + statusText,
        actionButton: BillAccountActionButtons.VIEW_PAYMENTS,
        statusIcon: BillAccountStatusIcons.CHECK,
        statusPillText: isScheduledPayment
          ? upcomingScheduledDate != null
            ? 'Scheduled ' + format(upcomingScheduledDate, 'MMM D')
            : 'Scheduled'
          : 'Processing',
        statusPillType: BillAccountPillType.SUCCESS
      };
    }

    // 3. Show Setup Autopay when someone has a single payment processing
    if (
      billAccountMetadata.hasSingleScheduledPayment &&
      billAccountMetadata.multipleScheduledPaymentsCount === 1 &&
      billAccountMetadata.scheduledPayments[0].paymentStatus === PaymentStatus.AUTHORIZED
    ) {
      return {
        statusText: '',
        actionButton: BillAccountActionButtons.ADD_AUTOPAY,
        statusIcon: BillAccountStatusIcons.CHECK,
        statusPillText:
          'Processing ' +
          format(_get(billAccountMetadata.scheduledPayments[0], 'receivedDate'), 'MMM D'),
        statusPillType: BillAccountPillType.SUCCESS
      };
    }

    // 4. Check for a single scheduled payment
    if (
      billAccountMetadata.hasSingleScheduledPayment &&
      billAccountMetadata.multipleScheduledPaymentsCount === 1
    ) {
      const isScheduledPayment =
        billAccountMetadata.scheduledPayments[0].paymentStatus === PaymentStatus.SCHEDULED;
      return {
        statusText: '',
        // Show edit payment button only in case of 'scheduled' payments, hide for 'authorized' payments.
        actionButton: BillAccountActionButtons.EDIT_PAYMENT,
        statusIcon: BillAccountStatusIcons.CHECK,
        statusPillText: isScheduledPayment
          ? 'Scheduled ' +
            format(_get(billAccountMetadata.scheduledPayments[0], 'receivedDate'), 'MMM D')
          : 'Processing ' +
            format(_get(billAccountMetadata.scheduledPayments[0], 'receivedDate'), 'MMM D'),
        statusPillType: BillAccountPillType.SUCCESS
      };
    }

    // 5. Check for Autopay Rules
    // Getting autopay error
    if (billAccountMetadata.hasAutoPayError) {
      return {
        statusText: BillAccountStatusText.FAILED_AUTOPAY_LOAD,
        actionButton: null,
        statusIcon: BillAccountStatusIcons.ALERT
      };
    }
    if (billAccountMetadata.hasAutoPay) {
      // If bill account is past due, do not display autopay details. Just display past due details.
      if (billAccountMetadata.isPastDue) {
        return {
          statusText: BillAccountStatusText.PAST_DUE,
          actionButton: BillAccountActionButtons.EDIT_AUTOPAY,
          statusIcon: BillAccountStatusIcons.ALERT,
          paymentNeeded: true,
          statusPillType: BillAccountPillType.ERROR
        };
      } else if (
        isValid(new Date(billAccountMetadata.automaticPayments.nextPaymentDate)) &&
        billAccountMetadata.automaticPayments.predictedDollarAmount !== 'UNKNOWN'
      ) {
        // If has autopay and next amount and date are known
        return {
          statusText: '',
          actionButton: BillAccountActionButtons.EDIT_AUTOPAY,
          statusIcon: BillAccountStatusIcons.CHECK,
          statusPillText:
            'Autopay ' + format(billAccountMetadata.automaticPayments.nextPaymentDate, 'MMM D'),
          statusPillType: BillAccountPillType.SUCCESS
        };
      } else {
        // If has autopay but next amount or date are not known
        return {
          statusText: '',
          actionButton: BillAccountActionButtons.EDIT_AUTOPAY,
          statusIcon: BillAccountStatusIcons.CHECK,
          statusPillText: 'Autopay',
          statusPillType: BillAccountPillType.SUCCESS
        };
      }
    }

    /*
        No payments scheduled - evaluate the status of the bill account
        Determine if the bill account is:
        1. Past Due
        2. Due Today
        3. Due soon
        4. Nothing Due ()
      */
    if (billAccountMetadata.hasTPIPayer && !billAccountMetadata.hasMinimumAmountDue) {
      if (Number(billAccountMetadata.accountBalance) > 0) {
        return {
          statusText: '',
          actionButton: BillAccountActionButtons.MAKE_PAYMENT,
          statusIcon: BillAccountStatusIcons.ALERT,
          paymentNeeded: true
        };
      }
      return {
        statusText: '',
        actionButton: BillAccountActionButtons.EDIT_LENDER,
        statusIcon: null,
        statusPillText: 'Paid by lender',
        statusPillType: BillAccountPillType.SUCCESS
      };
    }

    if (!billAccountMetadata.hasTPIPayer && !billAccountMetadata.hasMinimumAmountDue) {
      return {
        statusText: BillAccountStatusText.NOTHING_DUE,
        actionButton: BillAccountActionButtons.ADD_AUTOPAY,
        statusIcon: BillAccountStatusIcons.INFO,
        paymentNeeded: true,
        statusPillText: 'Paid',
        statusPillType: BillAccountPillType.SUCCESS
      };
    }

    // 1. Past Due according to the attribute on the bill account object
    if (billAccountMetadata.isPastDue) {
      return {
        statusText: BillAccountStatusText.PAST_DUE,
        actionButton: BillAccountActionButtons.MAKE_PAYMENT,
        statusIcon: BillAccountStatusIcons.ALERT,
        paymentNeeded: true,
        statusPillType: BillAccountPillType.ERROR
      };
    }

    // 2. Has a minimumAmountDue and a paymentDueDate that is today
    if (billAccountMetadata.hasMinimumAmountDue && billAccountMetadata.hasPaymentDueToday) {
      return {
        statusText: BillAccountStatusText.DUE_TODAY,
        actionButton: BillAccountActionButtons.MAKE_PAYMENT,
        statusIcon: BillAccountStatusIcons.INFO,
        paymentNeeded: true
      };
    }

    // 3. Has a minimumAmountDue and a paymentDueDate that is not today
    if (billAccountMetadata.hasMinimumAmountDue && !billAccountMetadata.hasPaymentDueToday) {
      return {
        statusText: '', //removed due_soon status msg
        actionButton: BillAccountActionButtons.MAKE_PAYMENT,
        statusIcon: BillAccountStatusIcons.INFO,
        paymentNeeded: true
      };
    }

    // 4. Nothing due Registered Bill Account
    if (billAccountMetadata.isOnlineBilling && billAccountMetadata.isRegisteredElseWhere) {
      return {
        statusText: BillAccountStatusText.NOTHING_DUE,
        actionButton: BillAccountActionButtons.ADD_AUTOPAY,
        statusIcon: null,
        paymentNeeded: true
      };
    }

    // Default Bill Account Card Treatment
    return {
      statusText: BillAccountStatusText.NOTHING_DUE,
      actionButton: BillAccountActionButtons.ADD_AUTOPAY,
      statusIcon: null
    };
  }

  getUpcomingScheduledDate(scheduledPayments) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const fixedPayments = scheduledPayments.map(payment => {
      const scheduledWithDate = {
        ...payment,
        parsedDate: new Date(payment.receivedDate)
      };
      return scheduledWithDate;
    });

    const upcoming = fixedPayments
      .filter(payment => payment.parsedDate >= today)
      .sort((pay1, pay2) => +pay1.parsedDate - +pay2.parsedDate)[0];

    return upcoming.parsedDate || null;
  }

  private getMostRecentPaymentDate(paymentHistory: Payment[], billAccountNumber: string): string {
    // .find() will work since we're sorting by date in the store. First entry
    // that matches bill account will be most recent
    const mostRecentPayment = paymentHistory.find(
      (payment: Payment) =>
        _get(payment, 'billAccounts[0].billAccountNumber') === billAccountNumber &&
        _get(payment, 'paymentStatus') === 'SUCCESSFUL'
    );
    return _get(mostRecentPayment, 'receivedDate', '');
  }

  private getScheduledPaymentsForBillAccount(
    billAccountNumber: string,
    scheduledPayments: ScheduledPayment[]
  ): ScheduledPayment[] {
    // Check multiple payments for each billing account.
    const matchingScheduledPayments: ScheduledPayment[] = [];
    scheduledPayments.forEach((payment: ScheduledPayment) => {
      payment.billAccounts.forEach(ba => {
        if (ba.billAccountNumber === billAccountNumber) {
          matchingScheduledPayments.push(payment);
        }
      });
    });

    return matchingScheduledPayments;
  }

  private getAutomaticPaymentForBillAccount(
    billAccountNumber: string,
    autopayPayments: AutomaticPayment[]
  ): AutomaticPayment {
    // Find matching autopay
    return (
      autopayPayments.find((autopay: AutomaticPayment) =>
        _get(autopay, 'billAccountNumber', '').includes(billAccountNumber)
      ) || null
    );
    // (force null if undefined)
  }

  // Check if any of the payments are associated with an expired credit card
  private hasExpiredPaymentAccounts(
    payments: Array<ScheduledPayment | AutomaticPayment>,
    paymentAccounts: PaymentAccount[]
  ): {
    expiredOrExpiringPaymentAccounts: string[];
    hasExpiringPaymentMethod: boolean;
    hasExpiredPaymentMethod: boolean;
  } {
    let hasExpiredCreditCard = false;
    let hasExpiringCreditCard = false;
    const expiredOrExpiringPaymentAccounts = new Array<string>();

    payments.forEach(payment => {
      const matchingPaymentAccount: PaymentAccount = paymentAccounts.find(
        paymentAccount => _get(payment, 'paymentAccount.nickName') === paymentAccount.nickName
      );
      if (_get(matchingPaymentAccount, 'expired')) {
        if (!expiredOrExpiringPaymentAccounts.includes(matchingPaymentAccount.paymentAccountId)) {
          expiredOrExpiringPaymentAccounts.push(matchingPaymentAccount.paymentAccountId);
        }
        hasExpiredCreditCard = true;
      }
      if (_get(matchingPaymentAccount, 'expiring')) {
        if (!expiredOrExpiringPaymentAccounts.includes(matchingPaymentAccount.paymentAccountId)) {
          expiredOrExpiringPaymentAccounts.push(matchingPaymentAccount.paymentAccountId);
        }
        hasExpiringCreditCard = true;
      }
    });
    return {
      expiredOrExpiringPaymentAccounts,
      hasExpiringPaymentMethod: hasExpiringCreditCard,
      hasExpiredPaymentMethod: hasExpiredCreditCard
    };
  }

  private hasTPIPayer(
    hasAutopay: boolean,
    billAccountsNotEligibleForAutoPay: BillAccount[],
    billAccount: BillAccount
  ): boolean {
    return (
      !hasAutopay &&
      billAccount.icon === PolicyTypeIconConstants.HOME &&
      _some(billAccountsNotEligibleForAutoPay, {
        billAccountNumber: billAccount.billAccountNumber
      })
    );
  }

  /**
   * TAKEN FROM ALERT SELECTORS - NEEDED THIS FUNTIONALITY TO MOVE TO LIBRARY
   * WITHOUT MOVING ALERTS
   */
  private hasUnsuccessfulPayment(
    billAccount: BillAccount,
    pendingAndSuccessfulPayments: ScheduledPayment[],
    failedPayments: ScheduledPayment[],
    automaticPayment: AutomaticPayment[]
  ): boolean {
    const alertableBillAccounts = [];
    const alertablePayments = [];

    // Determine for each bill account if there is an unsuccessful payment and if there are any payment actions made after that point
    // Skip bill accounts that are registered elsewhere
    if (billAccount.registeredElsewhere) {
      return false;
    }

    // Skip bill accounts that do not have an account balance, or if the account balance is negative.
    if (Number(billAccount.accountBalance) <= 0) {
      return false;
    }

    // Determine if there is a failed payment for this bill account
    const matchingFailedPayments = [];
    failedPayments.forEach(payment => {
      _get(payment, 'billAccounts', [] as BillAccountModel[]).forEach(billAccountForPayment => {
        if (
          _get(billAccountForPayment, 'billAccountNumber', '') === billAccount.billAccountNumber
        ) {
          matchingFailedPayments.push(payment);
        }
      });
    });

    // Early out of evaluating this bill account if there are no failed payments
    if (matchingFailedPayments.length === 0) {
      return false;
    }

    // Order failed payments by most recent first
    matchingFailedPayments
      .sort((a, b) => String(a['receivedDate']).localeCompare(String(b['receivedDate'])))
      .reverse();

    // Grab the most recent failed payment ordered by receivedDate (or empty string if not found)
    const mostRecentFailedPaymentDate = String(_get(matchingFailedPayments[0], 'receivedDate', ''));

    // Determine if there are any pending payments for this bill account
    const matchingPayments = [];
    pendingAndSuccessfulPayments.forEach(payment => {
      const billAccountsForPayment: BillAccountModel[] = _get(
        payment,
        'billAccounts',
        [] as BillAccountModel[]
      );
      billAccountsForPayment.forEach(billAccountForPayment => {
        if (
          _get(billAccountForPayment, 'billAccountNumber', '') === billAccount.billAccountNumber
        ) {
          matchingPayments.push(payment);
        }
      });
    });
    const defaultDate = '9999-01-01T00:00:00.000-0600';
    const initialDate = '0001-01-01';
    const isoDateFormat = 'YYYY-MM-DDTHH:mm:ss.SSSZ';

    // Order pending payments by most recent first
    matchingPayments
      .sort((a, b) => {
        // if the payment is SUCCESSFUL, we need to sort on the receivedDate field. For pending payments we sort on lastUpdateTimestamp
        // If a lastUpdateTimestamp starts with 0001-01-01 treat it as the most recent payment, so cast it to the year 9999
        const dateA =
          a.paymentStatus === 'SUCCESSFUL'
            ? _get(a, 'receivedDate', '')
            : _get(a, 'lastUpdateTimestamp', '').startsWith(initialDate)
              ? defaultDate
              : a.lastUpdateTimestamp;

        const dateB =
          b.paymentStatus === 'SUCCESSFUL'
            ? _get(b, 'receivedDate', '')
            : _get(b, 'lastUpdateTimestamp', '').startsWith(initialDate)
              ? defaultDate
              : b.lastUpdateTimestamp;
        return String(dateA).localeCompare(String(dateB));
      })
      .reverse();

    // Grab the most recent matching payment by lastUpdateTimestamp. Payments starting with 0001 are new and
    // need to be treated as the newest so we cast them to the year 9999
    const mostRecentScheduledDate = _get(matchingPayments[0], 'lastUpdateTimestamp', '').startsWith(
      initialDate
    )
      ? defaultDate
      : _get(matchingPayments[0], 'lastUpdateTimestamp', '');

    // Determine if there are any autopay rules for this bill account.
    // This currently will always be 1 autopay rule per bill account but that may change
    const matchingAutopay = automaticPayment.filter(
      autopay => _get(autopay, 'billAccountNumber') === billAccount.billAccountNumber
    );

    // Order autopay rules by most recent
    matchingAutopay
      .sort((a, b) => {
        // If the lastUpdateTimestamp starts with 0001-01-01 the treat it as the most recent payment
        const dateA = _get(a, 'lastUpdateTimestamp', '').startsWith(initialDate)
          ? defaultDate
          : a.lastUpdateTimestamp;
        const dateB = _get(b, 'lastUpdateTimestamp', '').startsWith(initialDate)
          ? defaultDate
          : b.lastUpdateTimestamp;
        return String(dateA).localeCompare(String(dateB));
      })
      .reverse();

    // Grab the most recent autopay rule by lastUpdateTimestamp. Autopay rules with a lastUpdateTimestamp starting with 0001 are new and
    // need to be treated as the newest item so we cast them to the year 9999
    const mostRecentAutopayDate = _get(matchingAutopay[0], 'lastUpdateTimestamp', '').startsWith(
      initialDate
    )
      ? defaultDate
      : _get(matchingAutopay[0], 'lastUpdateTimestamp', '');

    // Determine whether to show an alert for this bill account:
    // if the failed payment is valid and there is a more recent scheduled payment or automatic payment, suppress

    // Date() function in safari throws an error if the timezone offset field doesn't have a ":"
    // Ensure the dates are in the ISO format before passing to the Date() function.
    const failedPaymentDate = format(mostRecentFailedPaymentDate, isoDateFormat);
    const scheduledDate = format(mostRecentScheduledDate, isoDateFormat);
    const autopayDate = format(mostRecentAutopayDate, isoDateFormat);
    if (isValid(new Date(failedPaymentDate))) {
      if (
        (isValid(new Date(scheduledDate)) &&
          isAfter(mostRecentScheduledDate, mostRecentFailedPaymentDate)) ||
        (isValid(new Date(autopayDate)) &&
          isAfter(mostRecentAutopayDate, mostRecentFailedPaymentDate))
      ) {
        // Suppress the alert because of the scheduled payment or autopay rule
      } else {
        alertableBillAccounts.push(billAccount);
        // Make sure the payment isn't already being alerted on. This can happen for bundled payments
        const matchingAlertablePayment = alertablePayments.find(
          payment => payment.paymentId === _get(matchingFailedPayments[0], 'paymentId')
        );
        if (!matchingAlertablePayment) {
          alertablePayments.push(matchingFailedPayments[0]);
        }
      }
    }

    // Sending any alertable payments and all bill account data associated to them
    if (alertablePayments.length > 0) {
      return true;
    } else {
      return false;
    }
  }

  // Previous implementation that grabbed alerts from alert selectors and checked if they matched these
  // types. Replaced with hasUnsuccessfulPayment() (see above)
  /*
  // Determine if there is an unsuccessful payment we are alerting on for this bill account
  private hasUnsuccessfulPaymentAlert(
    billAccountNumber: string,
    unsuccessfulPayments: Alert[]
  ): boolean {
    let hasUnsuccessfulPaymentAlert = false;
    unsuccessfulPayments
      .filter(alert => {
        return (
          alert.type === AlertTypes.MULTIPLE_UNSUCCESSFUL_PAYMENTS ||
          AlertTypes.UNSUCCESSFUL_PAYMENT ||
          AlertTypes.UNSUCCESSFUL_AFT_PAYMENT
        );
      })
      .forEach(alert => {
        hasUnsuccessfulPaymentAlert = _some(alert.data.billAccounts, {
          billAccountNumber
        });
      });
    return hasUnsuccessfulPaymentAlert;
  }
  */
}
